import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { InboxIcon, CheckCircle2Icon, LayoutListIcon } from 'lucide-react';
import { CallbackRecord } from '../types';
import { RecordCard } from './RecordCard';
// Mock Data
const INITIAL_RECORDS: CallbackRecord[] = [{
  id: 'REC-001',
  contactName: 'Sarah Jenkins',
  phoneNumber: '(555) 019-2834',
  reason: 'Billing inquiry regarding double charge on last statement.',
  priority: 'high',
  requestedTime: '10:30 AM',
  status: 'available',
  missedCalls: 3,
  type: 'loan'
}, {
  id: 'REC-002',
  contactName: 'Michael Chen',
  phoneNumber: '(555) 837-9921',
  reason: 'Technical support needed for router configuration.',
  priority: 'medium',
  requestedTime: '11:15 AM',
  status: 'available',
  missedCalls: 1,
  type: 'lead'
}, {
  id: 'REC-003',
  contactName: 'Amanda Rodriguez',
  phoneNumber: '(555) 234-5678',
  reason: 'Wants to upgrade current subscription plan.',
  priority: 'low',
  requestedTime: '11:45 AM',
  status: 'available',
  missedCalls: 0,
  type: 'lead'
}, {
  id: 'REC-004',
  contactName: 'David Kim',
  phoneNumber: '(555) 987-6543',
  reason: 'Account locked out, needs password reset assistance.',
  priority: 'high',
  requestedTime: '12:05 PM',
  status: 'available',
  missedCalls: 5,
  type: 'loan'
}, {
  id: 'REC-005',
  contactName: 'Emily Watson',
  phoneNumber: '(555) 345-6789',
  reason: 'General question about new feature release.',
  priority: 'low',
  requestedTime: '12:30 PM',
  status: 'available',
  missedCalls: 2,
  type: 'lead'
}, {
  id: 'REC-006',
  contactName: 'James Wilson',
  phoneNumber: '(555) 876-5432',
  reason: 'Service cancellation request.',
  priority: 'medium',
  requestedTime: '1:00 PM',
  status: 'available',
  missedCalls: 1,
  type: 'loan'
}, {
  id: 'REC-007',
  contactName: 'Robert Taylor',
  phoneNumber: '(555) 111-2222',
  reason: 'Follow up on previous support ticket #88492.',
  priority: 'medium',
  requestedTime: '9:00 AM',
  status: 'claimed',
  claimedAt: '9:15 AM',
  missedCalls: 2,
  type: 'lead'
}, {
  id: 'REC-008',
  contactName: 'Lisa Anderson',
  phoneNumber: '(555) 333-4444',
  reason: 'Enterprise pricing inquiry.',
  priority: 'high',
  requestedTime: '9:30 AM',
  status: 'claimed',
  claimedAt: '9:45 AM',
  missedCalls: 4,
  type: 'loan'
}, {
  id: 'REC-009',
  contactName: 'William Martinez',
  phoneNumber: '(555) 555-6666',
  reason: 'Address update for hardware delivery.',
  priority: 'low',
  requestedTime: '10:00 AM',
  status: 'claimed',
  claimedAt: '10:20 AM',
  missedCalls: 0,
  type: 'lead'
}];
export function CallbackQueue() {
  const [records, setRecords] = useState<CallbackRecord[]>(INITIAL_RECORDS);
  const availableRecords = useMemo(() => records.filter(r => r.status === 'available'), [records]);
  const handleClaim = (id: string) => {
    const confirmed = window.confirm('Are you sure you want to claim this record?');
    if (!confirmed) return;
    const now = new Date().toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
    setRecords(prev => prev.map(record => record.id === id ? {
      ...record,
      status: 'claimed',
      claimedAt: now
    } : record));
  };
  const handleRelease = (id: string) => {
    setRecords(prev => prev.map(record => record.id === id ? {
      ...record,
      status: 'available',
      claimedAt: undefined
    } : record));
  };
  return <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8 font-sans" data-id="element-3">
      <div className="max-w-4xl mx-auto" data-id="element-4">
        {/* Header */}
        <div className="mb-8" data-id="element-5">
          <div className="flex items-center space-x-3 mb-2" data-id="element-6">
            <div className="bg-indigo-100 p-2 rounded-lg" data-id="element-7">
              <LayoutListIcon className="w-6 h-6 text-indigo-600" data-id="element-8" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" data-id="element-9">
              Callback Queue
            </h1>
          </div>
          <p className="text-slate-500 text-sm sm:text-base ml-11" data-id="element-10">
            Manage and process customer callback requests efficiently.
          </p>
        </div>

        {/* List Area */}
        <div className="relative min-h-[400px]" data-id="element-11">
          <AnimatePresence mode="popLayout" data-id="element-12">
            {availableRecords.length > 0 ? <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="space-y-4" data-id="element-13">
                <AnimatePresence data-id="element-14">
                  {availableRecords.map(record => <RecordCard key={record.id} record={record} actionType="claim" onAction={handleClaim} data-id="element-15" />)}
                </AnimatePresence>
              </motion.div> : <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.2
          }} className="absolute inset-0 flex flex-col items-center justify-center text-center p-8 bg-white rounded-xl border border-slate-200 border-dashed" data-id="element-16">
                <div className="bg-slate-50 p-4 rounded-full mb-4" data-id="element-17">
                  <CheckCircle2Icon className="w-8 h-8 text-slate-400" data-id="element-18" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-1" data-id="element-19">
                  Inbox Zero!
                </h3>
                <p className="text-slate-500 max-w-sm" data-id="element-20">
                  There are no available callbacks right now. Great job keeping
                  the queue clear!
                </p>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}